import { type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Plus } from "lucide-react";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

/**
 * Sticky Accounting Overview header rendered via AppLayout's pageHeader slot.
 * Breadcrumb + title + subtitle + two working header actions.
 *
 * The old "Register" button was removed: it routed to /accounting/register
 * which does not exist. Register lives in Bank transactions as "Go to bank
 * register" and in the Quick Actions sage panel as "Transaction".
 */

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
};

export function AccountingOverviewHeader() {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const wrapStyle: CSSProperties = {
    padding: isMobile ? "16px 16px 12px" : "24px 32px 16px",
  };

  const titleStyle: CSSProperties = {
    fontSize: isMobile ? 20 : 24,
    fontWeight: 800,
    margin: 0,
    color: PALETTE.ink,
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
  };

  const subtitleStyle: CSSProperties = {
    fontSize: isMobile ? 12 : 13,
    color: PALETTE.ink3,
    marginTop: 4,
    fontWeight: 500,
  };

  return (
    <div style={wrapStyle}>
      <div style={breadcrumbStyle}>
        <span>Accounting</span>
        <span style={sepStyle}>/</span>
        <span style={currentStyle}>Overview</span>
      </div>
      <div style={rowStyle}>
        <div style={titleBlockStyle}>
          <h1 style={titleStyle}>Overview</h1>
          <div style={subtitleStyle}>
            Record, categorize, and reconcile every transaction.
          </div>
        </div>
        <div style={actionsStyle}>
          <button
            type="button"
            style={linkBtnStyle}
            onClick={() => navigate("/accounting/chart-of-accounts")}
          >
            <BookOpen size={14} strokeWidth={2} />
            Chart of accounts
          </button>
          <button
            type="button"
            style={primaryBtnStyle}
            onClick={() => navigate("/accounting/bank-transactions?new=1")}
          >
            <Plus size={14} strokeWidth={2.5} />
            New transaction
          </button>
        </div>
      </div>
    </div>
  );
}

const breadcrumbStyle: CSSProperties = {
  fontSize: 12.5, color: PALETTE.ink3, fontWeight: 500, marginBottom: 4,
};

const sepStyle: CSSProperties = { color: PALETTE.ink4, margin: "0 5px" };

const currentStyle: CSSProperties = { color: PALETTE.ink, fontWeight: 600 };

const rowStyle: CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "flex-end",
  gap: 12, flexWrap: "wrap",
};

const titleBlockStyle: CSSProperties = { minWidth: 0 };

const actionsStyle: CSSProperties = {
  display: "flex", gap: 6, alignItems: "center",
  flexShrink: 0, flexWrap: "wrap",
};

const linkBtnStyle: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6,
  background: "transparent", border: 0,
  color: PALETTE.ink2, fontSize: 13, fontWeight: 500,
  padding: "7px 10px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
};

const primaryBtnStyle: CSSProperties = {
  background: PALETTE.ink, color: "#ffffff", border: 0,
  padding: "9px 14px", borderRadius: 8,
  fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
  display: "inline-flex", alignItems: "center", gap: 6,
  boxShadow: "0 1px 2px rgba(14, 26, 26, 0.08)",
};